import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Bell, X, Check } from "lucide-react";
import { Button } from "../components/ui/button";

const NotificationBell = () => {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (user && token) fetchNotifications();
    const interval = setInterval(() => {
      if (user && token) fetchNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, [user, token]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      // silently fail
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      // silently fail
    }
  };

  const stripHtml = (html) => html.replace(/<[^>]*>/g, "").substring(0, 100);

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-[#5C5D5C] hover:text-[#1A1B1A] transition-colors relative"
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#F06570] text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-12 w-80 bg-white border border-[#EAE6DF] shadow-lg z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between p-4 border-b border-[#EAE6DF]">
            <h3 className="font-medium text-[#1A1B1A]">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-[#20C997] hover:underline"
                data-testid="mark-all-read"
              >
                Mark all as read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-sm text-[#5C5D5C]">
                No notifications yet
              </div>
            ) : (
              notifications.map((notif, index) => (
                <div
                  key={notif.id}
                  className={`p-3 border-b border-[#EAE6DF] last:border-b-0 ${!notif.read ? "bg-[#F7F5F0]" : ""}`}
                  data-testid={`notification-${index}`}
                >
                  <p className="text-sm font-medium text-[#1A1B1A]">{notif.subject}</p>
                  <p className="text-xs text-[#5C5D5C] mt-1 line-clamp-2">{stripHtml(notif.message)}</p>
                  <p className="text-xs text-[#8E8F8E] mt-1">
                    {new Date(notif.created_at).toLocaleString("en-GB")}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
